import { useEffect, type ReactNode } from 'react';
import { useAuth } from '@context/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import Layout from '@components/Layout/Layout';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import ContainerSizeController from '@components/Layout/ContainerSizeController';
import { buildAgreementRedirectPath } from '@/utils/agreements';

interface AdminLayoutProps {
    children: ReactNode;
    title?: string;
    containerSize?: string; // Optional container size like "2200px"
}

const AdminLayout = ({ children, title = 'Admin - Project 314', containerSize }: AdminLayoutProps) => {
    const { isAuthenticated, isLoading, hasPendingRequiredAgreements, pendingRequiredAgreements, sessionPolicy } = useAuth();
    const location = useLocation();

    // Control loading spinner visibility based on authentication state
    useEffect(() => {
        if (isLoading) {
            showSpinner();
        } else {
            hideSpinner();
        }

        // Always hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, [isLoading]);

    // Show empty container while checking authentication
    if (isLoading) {
        return (
            <Layout title={title}>
                <ContainerSizeController size={containerSize} />
                <div className="container mx-auto my-4">
                    <div className="flex justify-center items-center" style={{ minHeight: '60vh' }}>
                        {/* LoadingSpinner is now controlled via the useEffect above */}
                    </div>
                </div>
            </Layout>
        );
    }

    // Only redirect if we're sure the user isn't authenticated (and we're done loading)
    if (!isAuthenticated) {
        // Preserve the entire URL including query parameters
        const currentUrl = encodeURIComponent(window.location.pathname + window.location.search);
        return <Navigate to={`/login?redirect=${currentUrl}`} />;
    }

    if (!sessionPolicy.canAccessAdminRoutes) {
        return <Navigate replace to="/" />;
    }

    if (hasPendingRequiredAgreements) {
        return (
            <Navigate
                replace
                to={buildAgreementRedirectPath({
                    pendingRequiredAgreements,
                    redirectTarget: location.pathname + location.search
                })}
            />
        );
    }

    return (
        <Layout title={title}>
            <ContainerSizeController size={containerSize} />
            <div className="container mx-auto my-4">
                {title && <h1 className="mb-4">{title}</h1>}
                <div className="admin-content">{children}</div>
            </div>
        </Layout>
    );
};

export default AdminLayout;
